import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { SealCheckIcon, ArrowSquareOutIcon } from "@phosphor-icons/react";

const GRADER_COLORS = {
  PSA: "bg-red-600 text-white",
  SGC: "bg-amber-500 text-zinc-900",
  BGS: "bg-black text-white",
  CGC: "bg-blue-600 text-white",
  StockX: "bg-emerald-600 text-white",
  Legit: "bg-purple-600 text-white",
  Other: "bg-zinc-800 text-white",
};

/**
 * Renders a compact verified-authentication badge for a listing. Hidden when
 * no verified cert exists — so it can be dropped into any listing page
 * unconditionally without cluttering unauthenticated items.
 */
export default function AuthenticatedBadge({ listingId, variant = "pill" }) {
  const [auth, setAuth] = useState(null);

  useEffect(() => {
    if (!listingId) return;
    api
      .get(`/listings/${listingId}/authentication`)
      .then((r) => setAuth(r.data))
      .catch(() => setAuth({ authenticated: false }));
  }, [listingId]);

  if (!auth?.authenticated) return null;

  const color = GRADER_COLORS[auth.grader] || GRADER_COLORS.Other;

  if (variant === "compact") {
    return (
      <span
        className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-mono font-bold tracking-widest uppercase ${color}`}
        title={`${auth.grader} cert ${auth.cert_number}`}
        data-testid="auth-badge-compact"
      >
        <SealCheckIcon size={10} weight="fill" />
        {auth.grader} {auth.cert_number}
      </span>
    );
  }

  return (
    <div
      className="mb-4 rounded-2xl border-2 border-emerald-400 bg-gradient-to-br from-emerald-50 to-white p-3 flex items-start gap-3"
      data-testid="auth-badge"
    >
      <div className={`h-10 w-10 rounded-xl grid place-items-center shrink-0 ${color}`}>
        <SealCheckIcon size={20} weight="fill" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-display font-black text-sm tracking-tight text-emerald-900">
          Authenticated by {auth.grader}
        </p>
        <p className="text-[11px] font-mono text-emerald-800/80 truncate">
          Cert {auth.cert_number}
        </p>
        {auth.verify_url && (
          <a
            href={auth.verify_url}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-bold text-emerald-700 hover:text-emerald-900"
            data-testid="auth-verify-link"
          >
            Verify on {auth.grader === "PSA" ? "psacard.com" : `${auth.grader}.com`}
            <ArrowSquareOutIcon size={11} weight="bold" />
          </a>
        )}
      </div>
    </div>
  );
}
